// API para obtener plagas por apartamento
import { API_URL } from './config';
import { getToken } from './auth';

const PESTS_CACHE_PREFIX = 'pwa_order_pests_cache';
const PESTS_CACHE_META_PREFIX = 'pwa_order_pests_meta';
const PESTS_CACHE_TTL_MS = 30000;

const normalizePests = (items = []) =>
  Array.isArray(items)
    ? items.map(p => ({ ...p, name: p.name || p.pest_name || p.pestName || p.label || p.title || 'Pest' }))
    : [];

const getCacheKey = orderId => `${PESTS_CACHE_PREFIX}_${orderId}`;
const getCacheMetaKey = orderId => `${PESTS_CACHE_META_PREFIX}_${orderId}`;

const getOrderVersion = order => order?.updatedAt || order?.updated_at || order?.updatedAtIso || order?.modifiedAt || order?.modified_at || null;

export function readOrderPestsCache(orderId) {
  if (!orderId) return {};
  try {
    const raw = localStorage.getItem(getCacheKey(orderId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function readOrderPestsMeta(orderId) {
  if (!orderId) return {};
  try {
    const raw = localStorage.getItem(getCacheMetaKey(orderId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function writeOrderPestsCache(orderId, pestsByApartment, orderVersion = null) {
  if (!orderId || !pestsByApartment || typeof pestsByApartment !== 'object') return;
  try {
    localStorage.setItem(getCacheKey(orderId), JSON.stringify(pestsByApartment));
    localStorage.setItem(getCacheMetaKey(orderId), JSON.stringify({
      updatedAt: Date.now(),
      orderVersion: orderVersion || null,
    }));
  } catch (e) {
    // El caché es opcional; si falla el almacenamiento no bloquea el flujo.
  }
}

export function invalidateOrderPestsCache(orderId) {
  if (!orderId) return;
  try {
    localStorage.removeItem(getCacheKey(orderId));
    localStorage.removeItem(getCacheMetaKey(orderId));
  } catch (e) {
    // Ignorar si no es posible limpiar la caché.
  }
}

const isOrderPestsCacheFresh = (orderId, orderVersion = null, ttlMs = PESTS_CACHE_TTL_MS) => {
  const cache = readOrderPestsMeta(orderId);
  if (!cache.updatedAt) return false;
  if (orderVersion && cache.orderVersion && String(cache.orderVersion) !== String(orderVersion)) {
    return false;
  }
  return Date.now() - Number(cache.updatedAt) < ttlMs;
};

export async function preloadOrderPests(orderId, apartments = [], options = {}) {
  if (!orderId || !Array.isArray(apartments) || !apartments.length) return {};

  const { orderVersion = null, force = false } = options;
  const cache = readOrderPestsCache(orderId);
  const cacheIsFresh = isOrderPestsCacheFresh(orderId, orderVersion);
  const pending = apartments.filter(ap => !ap.done && (!cache[ap.id] || force || !cacheIsFresh));

  if (!pending.length && !force && cacheIsFresh) return cache;

  const nextCache = { ...cache };
  const results = await Promise.all(
    pending.map(async (ap) => {
      const pests = await getPestsByApartment(orderId, ap.id, { force: force || !cacheIsFresh, useCache: true, orderVersion });
      nextCache[ap.id] = pests;
      return { id: ap.id, pests };
    })
  );

  if (results.length) writeOrderPestsCache(orderId, nextCache, orderVersion);
  if (!results.length && (force || !cacheIsFresh)) {
    writeOrderPestsCache(orderId, nextCache, orderVersion);
  }
  return nextCache;
}

export async function getPestsByApartment(orderId, apartmentId, options = {}) {
  const { force = false, useCache = true, orderVersion = null } = options;
  if (!orderId || !apartmentId) return [];

  if (useCache && !force && isOrderPestsCacheFresh(orderId, orderVersion)) {
    const cache = readOrderPestsCache(orderId);
    if (Array.isArray(cache[apartmentId])) {
      return normalizePests(cache[apartmentId]);
    }
  }

  const token = getToken();
  const res = await fetch(`${API_URL}/orders/${orderId}/pests/${apartmentId}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const cache = readOrderPestsCache(orderId);
    return Array.isArray(cache[apartmentId]) ? normalizePests(cache[apartmentId]) : [];
  }

  const data = await res.json();
  const pests = normalizePests(data?.data || []);

  if (useCache) {
    const nextCache = readOrderPestsCache(orderId);
    nextCache[apartmentId] = pests;
    writeOrderPestsCache(orderId, nextCache, orderVersion);
  }

  return pests;
}

export { getOrderVersion };

